import React, {useEffect, useState} from 'react';
import './filters.scss';
import {Select} from 'antd';
import CustomButton from '../custom-button/custom-button';
import {FilterConfig, radioButtonSplit} from '../../../model/filter';
import {SelectValue} from 'antd/es/select';
import MultiSelect from '../multi-select/multi-select';
import {searchFieldsToString} from '../../../utils/nestedSearchUtil';
import Arrow from '@svg/chevron-down_20.svg?react';
import {SystemConfiguration} from '../../../utils/system-configuration';
import FilterInput from './filter-input/filter-input';

interface FilterProps<T> {
    filterConfig: FilterConfig<T>;
    originalData: T[];
    setData: (data: T[]) => void;
}

interface FilterObject {
    searchVal: string;
    radioButtonActive: number;
    dropdownObj?: {[key: number]: number},
    multiDropDownObj?: {[key: number]: number[]}
}

export const Filters = <T,>({ filterConfig, originalData, setData }: FilterProps<T>) => {
    const { localStorageKey } = filterConfig || {};
    const [ filterObj, setFilterObj ] = useState<FilterObject>({
        searchVal: '',
        radioButtonActive: (localStorageKey && Number.parseInt(localStorage.getItem(localStorageKey) || '')) || filterConfig.radioButtons?.defaultSelectionIndex || 0,
        dropdownObj: {},
        multiDropDownObj: {}
    });
    const countriesMap = SystemConfiguration.getInstance().countryDtoMap;

    function getDropdownFn(selectedValues: number[], dropdownIndex: string) {
        return (item: any) => {
            if (!selectedValues.length) return true;

            let isFiltered = false;
            filterConfig?.multipleDropdownList?.[Number(dropdownIndex)]?.options
                .filter((opt, index) => selectedValues.includes(index)).forEach(opt => {
                isFiltered = isFiltered || opt.filterFn(item);
            });
            return isFiltered;
        };
    }

    useEffect(() => {

        if (originalData?.length) {
            const filterCallbacks: any[] = [];

            if (filterObj.searchVal) {
                const searchFn = (item: any) => searchFieldsToString(countriesMap, filterConfig.searchBox?.byFields, item).includes(filterObj.searchVal.toUpperCase());
                filterCallbacks.push(searchFn);
            }
            if (filterConfig.radioButtons?.options[filterObj.radioButtonActive]?.filterFn) {
                const radioFn = (item: any) => filterConfig.radioButtons?.options[filterObj.radioButtonActive]!.filterFn?.(item);
                filterCallbacks.push(radioFn);
            }
            if (filterObj.multiDropDownObj) {
                Object.entries(filterObj.multiDropDownObj).forEach(([dropdownIndex, selectedValues]) => {
                    const dropdownFn = getDropdownFn(selectedValues, dropdownIndex);
                    filterCallbacks.push(dropdownFn);
                });
            }
            if (filterObj.dropdownObj) {
                Object.entries(filterObj.dropdownObj).forEach(([dropdownIndex, optionIndex]) => {
                    const dropdownFn = (item: any) => filterConfig?.dropdownList?.[Number(dropdownIndex)]?.options[Number(optionIndex)]!.filterFn(item);
                    filterCallbacks.push(dropdownFn);
                });
            }
            const combinedCallback = (value: any) => filterCallbacks.every((callback: any) => callback(value));
            const copyData = originalData.filter(combinedCallback);
            setData(copyData);
        }
        else {
            setData([]);
        }
    }, [originalData, filterObj]);

    const onSearch = (event: any) => {
        setFilterObj({ ...filterObj, searchVal: event.target.value });
    };

    const onRadioClick = (index: number) => {
        setFilterObj({...filterObj, radioButtonActive: index});
        if (localStorageKey) {
            localStorage.setItem(localStorageKey, index.toString());
            window.dispatchEvent(new StorageEvent('storage', {key: localStorageKey, newValue: index.toString()}));
        }
    };

    const onSelect = (optionIndex: SelectValue, dropdownIndex: number) => {
        const dropdownObj = { ...filterObj.dropdownObj };
        dropdownObj[dropdownIndex] = optionIndex as number;
        setFilterObj({ ...filterObj, dropdownObj });

        if (localStorageKey) localStorage.setItem(localStorageKey,
            JSON.stringify([{filterIndex: 0, optionSelectedIndex: dropdownObj[0] || 0}, {filterIndex: 1, optionSelectedIndex: dropdownObj[1] || 0}])
        );
    };

    const onSelectMultiple = (selectedValues: string[], dropdownIndex: number) => {
        const multiDropDownObj = { ...filterObj.multiDropDownObj };
        multiDropDownObj[dropdownIndex] = selectedValues.map((_: any) => Number.parseInt(_));
        setFilterObj({ ...filterObj, multiDropDownObj });
    };

    return <div className="filter-container">
        {filterConfig.searchBox && <div className={'filter-input-container'}>
          <FilterInput
            placeholder={filterConfig.searchBox.placeholder}
            value={filterObj.searchVal}
            onChange={onSearch}
          />
        </div>}

        {filterConfig?.radioButtons?.options.map((option: any, index: number) => {
            if (option.label === radioButtonSplit) {
                return <div key={option.label} className={'split-line'}/>;
            }

            return <CustomButton key={option.label} label={option.label}
                                 className={`filter-btn ${filterObj?.radioButtonActive === index ? 'active' : ''}`}
                                 onClick={() => onRadioClick(index)}/>;
        })}

        {filterConfig?.dropdownList?.map((dropdown, dropdownIndex) =>
            <div key={dropdown.name} className={`dropdown-filter ${dropdown.name.toLowerCase().replace(/ /g, '-')}`}>
                <div className={'dropdown-name'}>{dropdown.name}</div>
                <Select defaultValue={{ value: 0, label: dropdown.options[0].label }}
                        onSelect={(optionValue: SelectValue) => {
                            onSelect(optionValue, dropdownIndex);
                        }}
                        className={`dropdown-${dropdown.name.toLowerCase().replace(/ /g, '-')}`}
                        popupClassName={'dropdown-custom'}
                        dropdownMatchSelectWidth={dropdown.matchWidth ?? true}
                        options={dropdown.options.map((option, index) => ({ ...option, value: index }))}
                        suffixIcon={<Arrow />}/>
            </div>
        )}

        {filterConfig?.multipleDropdownList?.map((multiDropdown, dropdownIndex) =>
            <div key={multiDropdown.name} className={'dropdown-filter'}>
                <div className={'dropdown-name'}>{multiDropdown.name}</div>
                {<MultiSelect showSearch={true}
                              showAllOption={true}
                              className={`${multiDropdown.name.toLowerCase()}`}
                              overlayClassName={`${multiDropdown.name.toLowerCase()}`}
                              options={multiDropdown.options.map((_, index) => ({ label: _.label, value: `${index}`}))}
                              selectedList={filterObj.multiDropDownObj?.[dropdownIndex]?.map(_ => `${_}`) || []}
                              onSelectionChange={(selectedValues) => onSelectMultiple(selectedValues, dropdownIndex)} />}
            </div>
        )}
    </div>;
};
